import {
  Box,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  FormControlLabel,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import type { WorkOrderType, WorkOrderStatus, WorkOrderPriority, WorkOrderFilters as WorkOrderFiltersType } from "../types";
import type { SelectChangeEvent } from "@mui/material";

interface WorkOrderFiltersProps {
  filters: WorkOrderFiltersType;
  onFiltersChange: (filters: WorkOrderFiltersType) => void;
  totalCount: number;
  filteredCount: number;
}

const typeOptions: { value: WorkOrderType; label: string }[] = [
  { value: "earth_moving", label: "Earth Moving" },
  { value: "road_works", label: "Road Works" },
  { value: "building_works", label: "Building" },
  { value: "machinery_parked", label: "Machinery" },
  { value: "agi_activity", label: "AGI" },
  { value: "marker_posts", label: "Marker Post" },
  { value: "forestry", label: "Forestry" },
  { value: "network_works", label: "Network" },
  { value: "other", label: "Other" },
];

const statusOptions: { value: WorkOrderStatus; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "dispatched", label: "Dispatched" },
  { value: "tech_aware", label: "Tech Aware" },
  { value: "on_site", label: "On Site" },
  { value: "completed", label: "Completed" },
  { value: "closed", label: "Closed" },
];

const priorityOptions: { value: WorkOrderPriority; label: string }[] = [
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

export const WorkOrderFilters = ({ filters, onFiltersChange, totalCount, filteredCount }: WorkOrderFiltersProps) => {
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ ...filters, search: event.target.value });
  };

  const handleTypeChange = (event: SelectChangeEvent<WorkOrderType[]>) => {
    const value = event.target.value;
    onFiltersChange({ ...filters, types: typeof value === "string" ? value.split(",") as WorkOrderType[] : value });
  };

  const handleStatusChange = (event: SelectChangeEvent<WorkOrderStatus[]>) => {
    const value = event.target.value;
    onFiltersChange({ ...filters, statuses: typeof value === "string" ? value.split(",") as WorkOrderStatus[] : value });
  };

  const handlePriorityChange = (event: SelectChangeEvent<WorkOrderPriority[]>) => {
    const value = event.target.value;
    onFiltersChange({ ...filters, priorities: typeof value === "string" ? value.split(",") as WorkOrderPriority[] : value });
  };

  const handleSLABreachedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ ...filters, slaBreached: event.target.checked });
  };

  const clearFilters = () => {
    onFiltersChange({
      search: "",
      types: [],
      statuses: [],
      priorities: [],
      zones: [],
      assignedToMe: false,
      slaBreached: false,
      dateRange: { start: null, end: null },
    });
  };

  const hasActiveFilters = filters.search || filters.types.length > 0 || filters.statuses.length > 0 || filters.priorities.length > 0 || filters.slaBreached;

  const renderValue = (selected: string[], options: { value: string; label: string }[]) => {
    if (selected.length === 0) return "";
    if (selected.length === 1) {
      return options.find(o => o.value === selected[0])?.label || selected[0];
    }
    return `${selected.length} selected`;
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            placeholder="Search work orders..."
            size="small"
            value={filters.search}
            onChange={handleSearchChange}
            sx={{ minWidth: 200, flex: "0 1 280px" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              },
            }}
          />

          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              multiple
              value={filters.priorities}
              onChange={handlePriorityChange}
              input={<OutlinedInput label="Priority" />}
              renderValue={(selected) => renderValue(selected, priorityOptions)}
            >
              {priorityOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={filters.priorities.includes(option.value)} size="small" />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={filters.statuses}
              onChange={handleStatusChange}
              input={<OutlinedInput label="Status" />}
              renderValue={(selected) => renderValue(selected, statusOptions)}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={filters.statuses.includes(option.value)} size="small" />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Type</InputLabel>
            <Select
              multiple
              value={filters.types}
              onChange={handleTypeChange}
              input={<OutlinedInput label="Type" />}
              renderValue={(selected) => renderValue(selected, typeOptions)}
            >
              {typeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={filters.types.includes(option.value)} size="small" />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={filters.slaBreached}
                onChange={handleSLABreachedChange}
                size="small"
                color="error"
              />
            }
            label={<Typography variant="body2">SLA Breached</Typography>}
          />

          {hasActiveFilters && (
            <Chip
              label="Clear"
              size="small"
              onDelete={clearFilters}
              deleteIcon={<CloseIcon />}
              variant="outlined"
            />
          )}

          <Box sx={{ flex: 1 }} />

          <Typography variant="body2" color="text.secondary">
            {filteredCount} of {totalCount}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};
